/**
 * DemoConfigPage
 *
 * Configuration page for Public Demo Links per agent.
 * Allows operators to generate, customize, and manage public demo URLs
 * for sharing chatbot demos without console access.
 *
 * Validates: Requirements 16.1–16.6, 18.1–18.5, 19.1–19.3
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete, TENANT_TOKEN_KEY } from '../../api/client';
import { Card, Button, Input, Modal } from '../../design-system';
import { tokens } from '../../design-system/tokens';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DemoConfig {
  id: string;
  virtualAgentId: string;
  token: string;
  enabled: boolean;
  config: {
    agentName: string;
    primaryColor: string;
    accentColor: string;
    welcomeMessage: string;
    logoUrl?: string;
    /** Título en la lista de conversaciones (consola) para esta demo. */
    conversationListTitle?: string;
    showPoweredBy: boolean;
    theme: 'light' | 'dark' | 'system';
  };
  maxMessagesPerMinute: number;
  maxConversationLength: number;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface DemoFormData {
  agentName: string;
  conversationListTitle: string;
  primaryColor: string;
  accentColor: string;
  welcomeMessage: string;
  logoUrl: string;
  showPoweredBy: boolean;
  theme: 'light' | 'dark' | 'system';
  maxMessagesPerMinute: number;
  maxConversationLength: number;
  expiresAt: string;
}

const EMPTY_FORM: DemoFormData = {
  agentName: '',
  conversationListTitle: '',
  primaryColor: '#7c3aed',
  accentColor: '#06b6d4',
  welcomeMessage: '¡Hola! ¿En qué puedo ayudarte?',
  logoUrl: '',
  showPoweredBy: true,
  theme: 'light',
  maxMessagesPerMinute: 10,
  maxConversationLength: 50,
  expiresAt: '',
};

// ─── Icons ───────────────────────────────────────────────────────────────────

function IconDemo() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCopy() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconRefresh() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="23 4 23 10 17 10" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3 6 5 6 21 6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── Live Preview Component ──────────────────────────────────────────────────

function DemoPreview({ form }: { form: DemoFormData }) {
  const bgColor = form.theme === 'dark' ? '#1e293b' : '#ffffff';
  const textColor = form.theme === 'dark' ? '#f1f5f9' : '#0f172a';
  const bubbleBg = form.theme === 'dark' ? '#334155' : '#f1f5f9';

  return (
    <div
      style={{
        width: 300,
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${tokens.colors.neutral[200]}`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        backgroundColor: bgColor,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '14px 16px',
          backgroundColor: form.primaryColor || '#7c3aed',
          color: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {form.logoUrl && (
          <img
            src={form.logoUrl}
            alt=""
            style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        )}
        <span style={{ fontWeight: 600, fontSize: 15 }}>
          {form.agentName || 'Agente'}
        </span>
      </div>

      {/* Chat body */}
      <div style={{ padding: 16, minHeight: 120 }}>
        {/* Welcome message bubble */}
        <div
          style={{
            backgroundColor: bubbleBg,
            color: textColor,
            padding: '10px 14px',
            borderRadius: '12px 12px 12px 4px',
            fontSize: 13,
            lineHeight: 1.4,
            maxWidth: '85%',
          }}
        >
          {form.welcomeMessage || '¡Hola! ¿En qué puedo ayudarte?'}
        </div>
      </div>

      {/* Input area mockup */}
      <div
        style={{
          padding: '10px 16px',
          borderTop: `1px solid ${form.theme === 'dark' ? '#475569' : tokens.colors.neutral[200]}`,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div
          style={{
            flex: 1,
            padding: '8px 12px',
            borderRadius: 20,
            backgroundColor: form.theme === 'dark' ? '#475569' : tokens.colors.neutral[100],
            color: form.theme === 'dark' ? '#94a3b8' : tokens.colors.neutral[400],
            fontSize: 13,
          }}
        >
          Escribe un mensaje...
        </div>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: form.accentColor || '#06b6d4',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" strokeLinecap="round" strokeLinejoin="round" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>

      {/* Powered by footer */}
      {form.showPoweredBy && (
        <div
          style={{
            padding: '6px 16px 10px',
            textAlign: 'center',
            fontSize: 11,
            color: form.theme === 'dark' ? '#64748b' : tokens.colors.neutral[400],
          }}
        >
          Powered by VirtualAgent
        </div>
      )}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DemoConfigPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  const [existingConfig, setExistingConfig] = useState<DemoConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState<DemoFormData>({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);

  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Regenerate confirmation
  const [regenerateModalOpen, setRegenerateModalOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  // ─── Data Loading ────────────────────────────────────────────────────────

  const loadConfig = useCallback(async () => {
    if (!agentId || !token) return;
    setLoading(true);
    setError('');
    try {
      const list = await apiGet<DemoConfig[]>(`/api/agents/${agentId}/demo`, token);
      const data = Array.isArray(list) && list.length > 0 ? list[0]! : null;
      if (data) {
        setExistingConfig(data);
        setForm({
          agentName: data.config.agentName || '',
          conversationListTitle: data.config.conversationListTitle?.trim() || '',
          primaryColor: data.config.primaryColor || '#7c3aed',
          accentColor: data.config.accentColor || '#06b6d4',
          welcomeMessage: data.config.welcomeMessage || '',
          logoUrl: data.config.logoUrl || '',
          showPoweredBy: data.config.showPoweredBy ?? true,
          theme: data.config.theme || 'light',
          maxMessagesPerMinute: data.maxMessagesPerMinute || 10,
          maxConversationLength: data.maxConversationLength || 50,
          expiresAt: data.expiresAt ? data.expiresAt.split('T')[0] : '',
        });
      }
    } catch (e) {
      if ((e as { status?: number }).status !== 404) {
        setError((e as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }, [agentId, token]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  // ─── Helpers ─────────────────────────────────────────────────────────────

  const getDemoUrl = () => {
    if (!existingConfig) return '';
    return `${window.location.origin}/demo/${existingConfig.token}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(getDemoUrl());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  // ─── Form Handlers ───────────────────────────────────────────────────────

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    if (!form.agentName.trim()) {
      errors.agentName = 'El nombre del agente es requerido';
    }
    if (!form.welcomeMessage.trim()) {
      errors.welcomeMessage = 'El mensaje de bienvenida es requerido';
    }
    if (form.maxMessagesPerMinute < 1) {
      errors.maxMessagesPerMinute = 'Mínimo 1 mensaje por minuto';
    }
    if (form.maxMessagesPerMinute > 60) {
      errors.maxMessagesPerMinute = 'Máximo 60 mensajes por minuto';
    }
    if (form.maxConversationLength < 1) {
      errors.maxConversationLength = 'Mínimo 1 mensaje';
    }
    if (form.maxConversationLength > 500) {
      errors.maxConversationLength = 'Máximo 500 mensajes';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGenerate = async () => {
    if (!validateForm() || !agentId || !token) return;
    setSaving(true);
    setFormErrors({});
    try {
      const payload = {
        config: {
          agentName: form.agentName.trim(),
          conversationListTitle: form.conversationListTitle.trim() || undefined,
          primaryColor: form.primaryColor,
          accentColor: form.accentColor,
          welcomeMessage: form.welcomeMessage.trim(),
          logoUrl: form.logoUrl.trim() || undefined,
          showPoweredBy: form.showPoweredBy,
          theme: form.theme,
        },
        maxMessagesPerMinute: form.maxMessagesPerMinute,
        maxConversationLength: form.maxConversationLength,
        expiresAt: form.expiresAt || undefined,
      };
      const created = await apiPost<DemoConfig>(
        `/api/agents/${agentId}/demo`,
        payload,
        token,
        { successMessage: 'Link de demo generado' }
      );
      setExistingConfig(created);
    } catch (e) {
      setFormErrors({ _general: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm() || !agentId || !token) return;
    setSaving(true);
    setFormErrors({});
    try {
      const payload = {
        config: {
          agentName: form.agentName.trim(),
          conversationListTitle: form.conversationListTitle.trim() || undefined,
          primaryColor: form.primaryColor,
          accentColor: form.accentColor,
          welcomeMessage: form.welcomeMessage.trim(),
          logoUrl: form.logoUrl.trim() || undefined,
          showPoweredBy: form.showPoweredBy,
          theme: form.theme,
        },
        maxMessagesPerMinute: form.maxMessagesPerMinute,
        maxConversationLength: form.maxConversationLength,
        expiresAt: form.expiresAt || undefined,
      };
      const updated = await apiPut<DemoConfig>(
        `/api/agents/${agentId}/demo`,
        payload,
        token,
        { successMessage: 'Configuración de demo actualizada' }
      );
      setExistingConfig(updated);
    } catch (e) {
      setFormErrors({ _general: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    if (!agentId || !token) return;
    setRegenerating(true);
    try {
      const payload = {
        config: {
          agentName: form.agentName.trim(),
          conversationListTitle: form.conversationListTitle.trim() || undefined,
          primaryColor: form.primaryColor,
          accentColor: form.accentColor,
          welcomeMessage: form.welcomeMessage.trim(),
          logoUrl: form.logoUrl.trim() || undefined,
          showPoweredBy: form.showPoweredBy,
          theme: form.theme,
        },
        maxMessagesPerMinute: form.maxMessagesPerMinute,
        maxConversationLength: form.maxConversationLength,
        expiresAt: form.expiresAt || undefined,
      };
      const created = await apiPost<DemoConfig>(
        `/api/agents/${agentId}/demo`,
        payload,
        token,
        { successMessage: 'Token regenerado — nueva URL generada' }
      );
      setExistingConfig(created);
      setRegenerateModalOpen(false);
    } catch (e) {
      setError((e as Error).message);
      setRegenerateModalOpen(false);
    } finally {
      setRegenerating(false);
    }
  };

  const handleToggleEnabled = async () => {
    if (!agentId || !token || !existingConfig) return;
    try {
      if (existingConfig.enabled) {
        await apiDelete<void>(
          `/api/agents/${agentId}/demo`,
          token,
          { successMessage: 'Demo deshabilitada' }
        );
        setExistingConfig({ ...existingConfig, enabled: false });
      } else {
        const updated = await apiPut<DemoConfig>(
          `/api/agents/${agentId}/demo`,
          { enabled: true },
          token,
          { successMessage: 'Demo habilitada' }
        );
        setExistingConfig(updated);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleDelete = async () => {
    if (!agentId || !token) return;
    setDeleting(true);
    try {
      await apiDelete(
        `/api/agents/${agentId}/demo`,
        token,
        { successMessage: 'Link de demo eliminado' }
      );
      setExistingConfig(null);
      setForm({ ...EMPTY_FORM });
      setDeleteModalOpen(false);
    } catch (e) {
      setError((e as Error).message);
      setDeleteModalOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  if (!token) return null;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to={`/app/agents/${agentId}/plugins`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 12,
            transition: `color ${tokens.transitions.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
          onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
        >
          <IconArrowLeft />
          Volver a plugins
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: tokens.borderRadius.lg,
                backgroundColor: tokens.colors.primary[100],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: tokens.colors.primary[600],
              }}
            >
              <IconDemo />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>
                Demo Pública
              </h1>
              <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[500] }}>
                Genera un link público para compartir tu chatbot
              </p>
            </div>
          </div>
          {existingConfig && (
            <Button variant="ghost" size="md" onClick={() => setDeleteModalOpen(true)}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: tokens.colors.error.main }}>
                <IconTrash /> Eliminar demo
              </span>
            </Button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 48, color: tokens.colors.neutral[500] }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite', marginRight: 12 }}>
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
          Cargando configuración...
        </div>
      )}

      {/* Content */}
      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {formErrors._general && (
            <div style={{ padding: '8px 12px', borderRadius: tokens.borderRadius.md, backgroundColor: tokens.colors.error.light, color: tokens.colors.error.dark, fontSize: 13 }}>
              {formErrors._general}
            </div>
          )}

          {/* Demo URL Section (only if exists) */}
          {existingConfig && (
            <Card variant="outlined" padding="lg">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
                  URL de Demo
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {/* Enable/Disable toggle */}
                  <button
                    type="button"
                    onClick={handleToggleEnabled}
                    style={{
                      padding: '6px 12px',
                      fontSize: 13,
                      fontWeight: 500,
                      borderRadius: tokens.borderRadius.md,
                      border: `1px solid ${existingConfig.enabled ? tokens.colors.success.main : tokens.colors.neutral[300]}`,
                      backgroundColor: existingConfig.enabled ? tokens.colors.success.light : tokens.colors.neutral[50],
                      color: existingConfig.enabled ? tokens.colors.success.dark : tokens.colors.neutral[600],
                      cursor: 'pointer',
                      transition: `all ${tokens.transitions.fast}`,
                    }}
                  >
                    {existingConfig.enabled ? '● Activa' : '○ Inactiva'}
                  </button>
                  <Button variant="ghost" size="sm" onClick={() => setRegenerateModalOpen(true)}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <IconRefresh /> Regenerar token
                    </span>
                  </Button>
                </div>
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 14px',
                  borderRadius: tokens.borderRadius.md,
                  backgroundColor: tokens.colors.neutral[50],
                  border: `1px solid ${tokens.colors.neutral[200]}`,
                }}
              >
                <code style={{ flex: 1, fontSize: 13, color: tokens.colors.neutral[800], wordBreak: 'break-all' }}>
                  {getDemoUrl()}
                </code>
                <Button variant="ghost" size="sm" onClick={handleCopy}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <IconCopy /> {copied ? 'Copiado!' : 'Copiar'}
                  </span>
                </Button>
              </div>
            </Card>
          )}

          {/* Appearance Section */}
          <Card variant="outlined" padding="lg">
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
              Apariencia
            </h2>
            <div style={{ display: 'flex', gap: 24 }}>
              {/* Form fields */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <Input
                  label="Nombre del Agente"
                  placeholder="Mi Asistente"
                  value={form.agentName}
                  onChange={(e) => setForm({ ...form, agentName: e.target.value })}
                  error={formErrors.agentName}
                  fullWidth
                />

                <Input
                  label="Título en conversaciones (opcional)"
                  placeholder="Ej. Demo sitio público · Verano 2026"
                  value={form.conversationListTitle}
                  onChange={(e) => setForm({ ...form, conversationListTitle: e.target.value })}
                  helperText="Se muestra en la lista de conversaciones de la consola para chats de esta demo."
                  fullWidth
                />

                {/* Color pickers */}
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                      Color Primario
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="color"
                        value={form.primaryColor}
                        onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                        style={{ width: 40, height: 36, border: 'none', borderRadius: tokens.borderRadius.md, cursor: 'pointer', padding: 0 }}
                      />
                      <input
                        type="text"
                        value={form.primaryColor}
                        onChange={(e) => setForm({ ...form, primaryColor: e.target.value })}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          fontSize: 14,
                          borderRadius: tokens.borderRadius.md,
                          border: `1px solid ${tokens.colors.neutral[300]}`,
                          backgroundColor: tokens.colors.neutral[50],
                          color: tokens.colors.neutral[900],
                          outline: 'none',
                          fontFamily: tokens.typography.fontFamily.mono,
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                      Color de Acento
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="color"
                        value={form.accentColor}
                        onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
                        style={{ width: 40, height: 36, border: 'none', borderRadius: tokens.borderRadius.md, cursor: 'pointer', padding: 0 }}
                      />
                      <input
                        type="text"
                        value={form.accentColor}
                        onChange={(e) => setForm({ ...form, accentColor: e.target.value })}
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          fontSize: 14,
                          borderRadius: tokens.borderRadius.md,
                          border: `1px solid ${tokens.colors.neutral[300]}`,
                          backgroundColor: tokens.colors.neutral[50],
                          color: tokens.colors.neutral[900],
                          outline: 'none',
                          fontFamily: tokens.typography.fontFamily.mono,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Welcome message */}
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                    Mensaje de Bienvenida
                  </label>
                  <textarea
                    value={form.welcomeMessage}
                    onChange={(e) => setForm({ ...form, welcomeMessage: e.target.value })}
                    placeholder="¡Hola! ¿En qué puedo ayudarte?"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 14,
                      borderRadius: tokens.borderRadius.md,
                      border: `1px solid ${formErrors.welcomeMessage ? tokens.colors.error.main : tokens.colors.neutral[300]}`,
                      backgroundColor: tokens.colors.neutral[50],
                      color: tokens.colors.neutral[900],
                      outline: 'none',
                      resize: 'vertical',
                      fontFamily: tokens.typography.fontFamily.sans,
                    }}
                  />
                  {formErrors.welcomeMessage && (
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: tokens.colors.error.dark }}>{formErrors.welcomeMessage}</p>
                  )}
                </div>

                {/* Logo URL */}
                <Input
                  label="URL del Logo (opcional)"
                  placeholder="https://ejemplo.com/logo.png"
                  value={form.logoUrl}
                  onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                  fullWidth
                />

                {/* Show Powered By */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    id="showPoweredBy"
                    checked={form.showPoweredBy}
                    onChange={(e) => setForm({ ...form, showPoweredBy: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: 'pointer' }}
                  />
                  <label htmlFor="showPoweredBy" style={{ fontSize: 14, color: tokens.colors.neutral[700], cursor: 'pointer' }}>
                    Mostrar "Powered by" en el footer
                  </label>
                </div>

                {/* Theme */}
                <div>
                  <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                    Tema
                  </label>
                  <select
                    value={form.theme}
                    onChange={(e) => setForm({ ...form, theme: e.target.value as 'light' | 'dark' | 'system' })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 14,
                      borderRadius: tokens.borderRadius.md,
                      border: `1px solid ${tokens.colors.neutral[300]}`,
                      backgroundColor: tokens.colors.neutral[50],
                      color: tokens.colors.neutral[900],
                      outline: 'none',
                    }}
                  >
                    <option value="light">Claro</option>
                    <option value="dark">Oscuro</option>
                    <option value="system">Sistema</option>
                  </select>
                </div>
              </div>

              {/* Live Preview */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: tokens.colors.neutral[500] }}>Vista previa</span>
                <DemoPreview form={form} />
              </div>
            </div>
          </Card>

          {/* Limits Section */}
          <Card variant="outlined" padding="lg">
            <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600, color: tokens.colors.neutral[900] }}>
              Límites
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Input
                label="Máximo de mensajes por minuto"
                type="number"
                min={1}
                max={60}
                value={form.maxMessagesPerMinute}
                onChange={(e) => setForm({ ...form, maxMessagesPerMinute: parseInt(e.target.value) || 10 })}
                error={formErrors.maxMessagesPerMinute}
                helperText="Límite de mensajes que un visitante puede enviar por minuto (1–60)"
                fullWidth
              />
              <Input
                label="Largo máximo de conversación"
                type="number"
                min={1}
                max={500}
                value={form.maxConversationLength}
                onChange={(e) => setForm({ ...form, maxConversationLength: parseInt(e.target.value) || 50 })}
                error={formErrors.maxConversationLength}
                helperText="Cantidad máxima de mensajes por sesión de demo (1–500)"
                fullWidth
              />
              <div>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 500, color: tokens.colors.neutral[700], marginBottom: 4 }}>
                  Fecha de expiración (opcional)
                </label>
                <input
                  type="date"
                  value={form.expiresAt}
                  onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    fontSize: 14,
                    borderRadius: tokens.borderRadius.md,
                    border: `1px solid ${tokens.colors.neutral[300]}`,
                    backgroundColor: tokens.colors.neutral[50],
                    color: tokens.colors.neutral[900],
                    outline: 'none',
                  }}
                />
                <p style={{ margin: '4px 0 0', fontSize: 12, color: tokens.colors.neutral[500] }}>
                  Si se establece, el link dejará de funcionar después de esta fecha
                </p>
              </div>
            </div>
          </Card>

          {/* Save / Generate Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button
              variant="primary"
              size="md"
              onClick={existingConfig ? handleUpdate : handleGenerate}
              disabled={saving}
            >
              {saving
                ? 'Guardando...'
                : existingConfig
                  ? 'Actualizar Configuración'
                  : 'Generar Link de Demo'}
            </Button>
          </div>
        </div>
      )}

      {/* Regenerate Confirmation Modal */}
      <Modal
        isOpen={regenerateModalOpen}
        onClose={() => setRegenerateModalOpen(false)}
        title="Regenerar Token"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={() => setRegenerateModalOpen(false)} disabled={regenerating}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleRegenerate} disabled={regenerating}>
              {regenerating ? 'Regenerando...' : 'Regenerar'}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: tokens.colors.neutral[700] }}>
          ¿Estás seguro de que deseas regenerar el token? La URL actual dejará de funcionar y se generará una nueva.
        </p>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Eliminar Demo Pública"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="ghost" size="md" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button variant="primary" size="md" onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </div>
        }
      >
        <p style={{ margin: 0, color: tokens.colors.neutral[700] }}>
          ¿Estás seguro de que deseas eliminar el link de demo? La URL dejará de funcionar y esta acción no se puede deshacer.
        </p>
      </Modal>

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default DemoConfigPage;
